import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  useDisclosure,
  Text,
  useToast,
} from "@chakra-ui/react";
import Navbar from "../../Components/Navbar";
import { UserContext } from "../../contexts/User";
import UserRegistrationModal from "../../Components/createUserModal";

const Users = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { listUsersApi, deleteUserApi } = useContext(UserContext);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Estados para armazenar os dados e a paginação de cada role
  const [users, setUsers] = useState({ role_1: [], role_2: [], role_3: [] });
  const [page, setPage] = useState({ role_1: 1, role_2: 1, role_3: 1 });
  const [totalPages, setTotalPages] = useState({
    role_1: 1,
    role_2: 1,
    role_3: 1,
  });

  // Função que busca os dados para uma role específica e atualiza seus estados
  const getUsersByRole = async (role, pageNumber) => {
    try {
      // Chama a API passando o número da página desejada (a função listUsersApi deve estar ajustada para receber o parâmetro)
      const usersData = await listUsersApi(pageNumber);
      // A resposta da API vem dentro de data[0] com as chaves role_1, role_2 e role_3
      const roleData = usersData.data[0][role];
      setUsers((prev) => ({ ...prev, [role]: roleData }));
      setTotalPages((prev) => ({
        ...prev,
        [role]: roleData.last_page,
      }));
      setPage((prev) => ({ ...prev, [role]: roleData.current_page }));
    } catch (error) {
      console.log(error);
    }
  };

  // Carrega a primeira página de cada role na inicialização
  useEffect(() => {
    ["role_1", "role_2", "role_3"].forEach((role) => {
      getUsersByRole(role, 1);
    });
  }, []);

  // Atualiza a página de uma role específica ao clicar em "Anterior" ou "Próximo"
  const handlePageChange = (role, direction) => {
    const newPage = direction === "next" ? page[role] + 1 : page[role] - 1;
    getUsersByRole(role, newPage);
  };

  // Ao excluir, recarrega os dados da página atual de cada role
  const deleteUser = async (userId) => {
    try {
      const deleteUserResponse = await deleteUserApi(userId);
      if (deleteUserResponse.status === 200) {
        toast({
          title: "Usuário Removido",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        ["role_1", "role_2", "role_3"].forEach((role) => {
          getUsersByRole(role, page[role]);
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao remover usuário",
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Navbar />
      <Box p={8} ml={{ base: 0, md: 60 }} bgColor={"#f0f8ff"} minH={"100vh"}>
        <Flex justifyContent="space-between">
          <Heading as="h1" size="xl">
            Lista de Usuários
          </Heading>
          <Button colorScheme="teal" onClick={onOpen}>
            Adicionar Usuário
          </Button>
        </Flex>

        {Object.entries(users).map(
          ([role, userList]) =>
            userList?.data?.length > 0 && (
              <Box key={role} mt={8}>
                <Heading size="md" mb={4}>
                  {role === "role_1"
                    ? "Lista de Admins"
                    : role === "role_2"
                    ? "Lista de Professores"
                    : "Lista de Alunos"}
                </Heading>
                <Table variant="striped">
                  <Thead>
                    <Tr>
                      <Th>ID</Th>
                      <Th>Nome</Th>
                      <Th>E-mail</Th>
                      {role === "role_2" && <Th>Qtde de Questões</Th>}
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {userList.data.map((user) => (
                      <Tr key={user.id}>
                        <Td>{user.id}</Td>
                        <Td>{user.name}</Td>
                        <Td>{user.email}</Td>
                        {role === "role_2" && <Td>{user.questions_count}</Td>}
                        <Td>
                          <Button
                            colorScheme="teal"
                            size="sm"
                            onClick={() => {
                              localStorage.setItem("userInfoID", user.id);
                              navigate(`/user/${user.name}`);
                            }}
                          >
                            Ver perfil
                          </Button>
                          {user.role_id !== 2 && (
                            <Button
                              ml={5}
                              size="sm"
                              colorScheme="red"
                              onClick={() => deleteUser(user.id)}
                            >
                              Excluir
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>

                <Flex justifyContent="center" mt={4}>
                  <Button
                    onClick={() => handlePageChange(role, "prev")}
                    isDisabled={page[role] === 1}
                    mr={2}
                  >
                    Anterior
                  </Button>
                  <Text mt={2}>
                    Página {page[role]} de {totalPages[role]}
                  </Text>

                  <Button
                    onClick={() => handlePageChange(role, "next")}
                    isDisabled={page[role] === totalPages[role]}
                    ml={2}
                  >
                    Próximo
                  </Button>
                </Flex>
              </Box>
            )
        )}
      </Box>

      <UserRegistrationModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          // Após criar um usuário, atualiza cada role na página atual
          ["role_1", "role_2", "role_3"].forEach((role) => {
            getUsersByRole(role, page[role]);
          });
        }}
      />
    </>
  );
};

export default Users;
